import { BlogListingPage } from '@/lib/pages/blog/BlogListingPage';
import { BlogUnavailablePage } from '@/lib/pages/blog/BlogUnavailablePage';
import {
  isBlogUnavailable,
  type BlogUnavailableProps,
} from '@/lib/pages/blog/blog-unavailable';
import { blogUnavailableProps } from '@/lib/pages/blog/blog-unavailable-props';
import {
  loadOptionalBlogValue,
  settleBlogPrimary,
} from '@/lib/pages/blog/settle-blog-primary';

import {
  BlogPublicCategory,
  BlogPublicPost,
  blogCategoryListingSeo,
  blogListingHref,
  fetchBlogCategory,
  fetchBlogHub,
  parseUiPage,
  SpringPage,
  toApiPage,
} from '@/lib/entities/blog';
import { fetchPageSeoOrNull, PageSeo } from '@/lib/entities/seo';
import { SeoEntries } from '@/lib/entities/seo/lib/seo-entries';

import { GetServerSideProps } from 'next';
import { i18n, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { isDevelopment } from '../../../lib/config';

type ReadyProps = {
  categories: BlogPublicCategory[];
  tags: string[];
  listing: SpringPage<BlogPublicPost>;
  categorySlug: string;
  categoryTitle: string;
  categoryMetaTitle: string | null;
  categoryMetaDescription: string | null;
  uiPage: number;
  pageSeo: PageSeo | null;
  locale: string;
};

type Props = ReadyProps | BlogUnavailableProps;

export default function BlogCategoryPage(props: Props) {
  if (isBlogUnavailable(props)) {
    return <BlogUnavailablePage path={props.path} />;
  }

  return <BlogCategoryView {...props} />;
}

const BlogCategoryView = ({
  categories,
  tags,
  listing,
  categorySlug,
  categoryTitle,
  categoryMetaTitle,
  categoryMetaDescription,
  uiPage,
  pageSeo,
  locale,
}: ReadyProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('index', { keyPrefix: 'meta' });

  const path = `/blog/${categorySlug}` as `/${string}`;
  const listingSeo = blogCategoryListingSeo({
    categoryTitle,
    metaTitle: categoryMetaTitle,
    metaDescription: categoryMetaDescription,
    pageTitle: pageSeo?.title,
    pageDescription: pageSeo?.description,
    blogTitle: t('blog.title'),
    blogDescription: t('blog.description'),
  });

  return (
    <>
      <SeoEntries
        fallback={{
          title: listingSeo.title,
          description: listingSeo.description,
          openGraphImage: '/images/loyalty-program/og.webp',
        }}
        seo={
          pageSeo
            ? {
                ...pageSeo,
                title: listingSeo.title,
                description: listingSeo.description,
              }
            : null
        }
        language={language}
        path={path}
        robots={uiPage > 1 ? 'noindex, follow' : undefined}
      />
      <main>
        <BlogListingPage
          locale={locale}
          categories={categories}
          tags={tags}
          posts={listing.content}
          currentPage={uiPage}
          totalPages={listing.totalPages}
          hrefForPage={(page) => blogListingHref(`/blog/${categorySlug}`, page)}
          title={categoryTitle}
          breadcrumbCurrent={categoryTitle}
          activeCategorySlug={categorySlug}
        />
      </main>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async ({
  locale,
  params,
  query,
  res,
}) => {
  if (locale === 'default') {
    return { notFound: true };
  }

  const categorySlug =
    typeof params?.category === 'string' ? params.category : '';

  if (!categorySlug) {
    return { notFound: true };
  }

  if (isDevelopment) {
    await i18n?.reloadResources();
  }

  const lang = locale ?? 'ru';
  const uiPage = parseUiPage(query.page);
  const path = `/blog/${categorySlug}`;
  const listingOutcome = await settleBlogPrimary(() =>
    fetchBlogCategory(categorySlug, lang, toApiPage(uiPage))
  );

  if (listingOutcome.status === 'unavailable') {
    return {
      props: await blogUnavailableProps(
        res,
        lang,
        `/blog/${categorySlug}` as `/${string}`
      ),
    };
  }

  if (listingOutcome.status === 'notFound') {
    return { notFound: true };
  }

  const hub = await loadOptionalBlogValue(() => fetchBlogHub(lang));
  const category = hub?.categories.find((item) => item.slug === categorySlug);

  if (hub && !category) {
    return { notFound: true };
  }

  const pageSeo = await fetchPageSeoOrNull({ path, language: lang });

  return {
    props: {
      ...(await serverSideTranslations(lang, ['index'])),
      headerTheme: 'light',
      categories: hub?.categories ?? [],
      tags: hub?.tags ?? [],
      listing: listingOutcome.data,
      categorySlug,
      categoryTitle: category?.title ?? categorySlug,
      categoryMetaTitle: category?.metaTitle ?? null,
      categoryMetaDescription: category?.metaDescription ?? null,
      uiPage,
      pageSeo,
      locale: lang,
    },
  };
};
